export const GALLERY_LIMIT = 4

/**
 * Caps the stored URL for a ~100px box. Other write paths store an uncapped master, so
 * append the param when it is absent rather than passing the original through.
 */
const thumbSrc = (url) => {
  if (!url) return null
  if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1200')
  return `${url}${url.includes('?') ? '&' : '?'}width=200`
}

const Thumbnail = ({ url, overflowCount }) => (
  <div class="relative aspect-square flex-1 overflow-hidden rounded bg-neutrals-image-grey">
    {url && <img src={url} loading="lazy" alt="" class="h-full w-full object-cover object-bottom" />}
    {overflowCount > 0 && (
      <div class="absolute inset-0 flex items-center justify-center bg-black/60">
        <div class="font-bold text-primary-white paragraph-lg">+{overflowCount}</div>
      </div>
    )}
  </div>
)

/**
 * One saveable list. The size/quantity line is not in the design: with per-list
 * sizing two enabled rows would otherwise render identically (spec section 7).
 */
export const WishlistListRow = ({ list, entry, isActive, sizeLabel, error, disabled, onToggle, onActivate }) => {
  const items = list.items ?? []
  const avatar = list.profile?.connector?.avatar_url
  const overflow = items.length - GALLERY_LIMIT

  return (
    <div
      onClick={disabled ? undefined : () => onActivate(list.id)}
      data-role="wishlist-drawer-row"
      data-list-id={list.id}
      data-enabled={entry.enabled ? 'true' : 'false'}
      data-active={isActive ? 'true' : 'false'}
      class={`flex flex-col gap-3 rounded border bg-primary-white p-3 ${
        entry.enabled ? 'border-secondary-green' : 'border-neutrals-medium-grey'
      }`}
    >
      <div class="flex flex-row items-center gap-2">
        {/* Arbitrary sizes: the config defines 5.5 under `width` only, so h-5.5 generates nothing. */}
        {avatar && <img src={avatar} loading="lazy" alt="" class="h-[22px] w-[22px] shrink-0 rounded-full" />}

        <div class="flex min-w-0 flex-1 flex-col">
          <div class="truncate font-bold">{list.name}</div>
          {entry.enabled && (
            <div
              data-role="wishlist-drawer-row-summary"
              class={`paragraph-xs ${isActive ? 'text-primary-black' : 'text-neutrals-secondary-grey'}`}
            >
              {sizeLabel ? `${sizeLabel} · Qty ${entry.quantity}` : `Qty ${entry.quantity}`}
            </div>
          )}
        </div>

        <button
          type="button"
          role="switch"
          disabled={disabled}
          aria-checked={entry.enabled}
          aria-label={`${entry.enabled ? 'Remove from' : 'Add to'} ${list.name}`}
          data-role="wishlist-drawer-toggle"
          onClick={(event) => {
            event.stopPropagation()
            onToggle(list.id)
          }}
          class={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
            entry.enabled ? 'bg-secondary-green' : 'bg-neutrals-medium-grey'
          }`}
        >
          <span
            class={`absolute top-0.5 h-5 w-5 rounded-full bg-primary-white transition-all ${
              entry.enabled ? 'left-[22px]' : 'left-0.5'
            }`}
          />
        </button>
      </div>

      <div class="flex flex-row gap-2">
        {Array.from({ length: GALLERY_LIMIT }, (_, index) => (
          <Thumbnail
            key={index}
            url={thumbSrc(items[index]?.meta?.img)}
            overflowCount={index === GALLERY_LIMIT - 1 && overflow > 0 ? overflow + 1 : 0}
          />
        ))}
      </div>

      {error && (
        <div data-role="wishlist-drawer-row-error" class="text-system-error paragraph-xs">
          {error}
        </div>
      )}
    </div>
  )
}

/** Same shape as a real row, so the panel does not resize when data lands. */
export const WishlistRowSkeleton = () => (
  <div class="flex flex-col gap-3 rounded border border-neutrals-medium-grey p-3" data-role="wishlist-drawer-skeleton">
    <div class="flex flex-row items-center gap-2">
      <div class="h-[22px] w-[22px] shrink-0 rounded-full bg-neutrals-image-grey" />
      <div class="h-4 w-32 bg-neutrals-image-grey" />
    </div>
    <div class="flex flex-row gap-2">
      {Array.from({ length: GALLERY_LIMIT }, (_, index) => (
        <div key={index} class="aspect-square flex-1 rounded bg-neutrals-image-grey" />
      ))}
    </div>
  </div>
)
